import {
  AudioObjectPaginatedList,
  AudiosAudioIdDeleteRequest,
  AudiosAudioIdPatchRequest,
  AudiosPostRequest,
} from '@nativewaves/nw-cloud-sdk/content'
import { InfiniteData, useMutation, useQueryClient } from '@tanstack/react-query'

import { ContentAPI } from 'services/api'
import { audioObjectQueryKeys } from 'services/queryKeys'
import { updateListCachesByCreation, updateListCachesByDeletion, updateListCachesByUpdate } from 'utils'

export const audioObjectMutationKeys = {
  delete: () => ['content', 'audio-object', 'DELETE'] as const,
}

type UseAudioObjectCreateMutationProps = {
  originId?: string
}

export const useAudioObjectCreateMutation = (props: UseAudioObjectCreateMutationProps) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (params: AudiosPostRequest) => ContentAPI.audioObjects.audiosPost(params),
    onMutate: async () => {
      await queryClient.cancelQueries({
        queryKey: audioObjectQueryKeys.lists(),
      })
    },
    onSettled: (data, error) => {
      if (!!data && !error) {
        queryClient.setQueriesData<InfiniteData<AudioObjectPaginatedList>>(
          { queryKey: audioObjectQueryKeys.lists() },
          updateListCachesByCreation(data, { idKey: 'id', originId: props.originId }),
        )
      }
    },
  })
}

export const useAudioObjectUpdateMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (params: AudiosAudioIdPatchRequest) => ContentAPI.audioObjects.audiosAudioIdPatch(params),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: audioObjectQueryKeys.batchGets() })
    },
    onSettled: (data, error, variables) => {
      if (!error && !!data) {
        queryClient.resetQueries({ queryKey: audioObjectQueryKeys.batchGets() })

        queryClient.setQueriesData<InfiniteData<AudioObjectPaginatedList>>(
          { queryKey: audioObjectQueryKeys.lists() },
          updateListCachesByUpdate(data, { idKey: 'id', idToMatch: variables.audioId }),
        )

        return queryClient.invalidateQueries({ queryKey: audioObjectQueryKeys.details() })
      }
    },
  })
}

export const useAudioObjectDeleteMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: audioObjectMutationKeys.delete(),
    mutationFn: (params: AudiosAudioIdDeleteRequest) => ContentAPI.audioObjects.audiosAudioIdDelete(params),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: audioObjectQueryKeys.lists() })
      await queryClient.cancelQueries({ queryKey: audioObjectQueryKeys.batchGets() })
    },
    onSettled: (_, error, variables) => {
      if (!error) {
        queryClient.setQueriesData<InfiniteData<AudioObjectPaginatedList>>(
          { queryKey: audioObjectQueryKeys.lists() },
          updateListCachesByDeletion({ idKey: 'id', idToMatch: variables.audioId }),
        )
      }
    },
  })
}
